import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import type { ApplicationDetail } from 'src/api/main'
import { client } from 'src/api/main/client.gen'

/**
 * Hand-rolled wrapper for `POST /applications/third-party/`. Once the backend
 * schema change (added `settings` to `ApplicationUpdate`, plus the existing
 * `ThirdPartyApplicationCreateView`) is deployed, regenerate the API client
 * (`npm run openapi`) and replace callers with the generated
 * `applicationsThirdPartyCreate{,Mutation}` symbols.
 */

export type ApplicationsThirdPartyCreateBody = {
  organization: number
  application_name: string
  application_description?: string
  /** Authoritative host used for redirect / CORS scoping. */
  application_domain?: string
  /** User-facing launch URL for the third-party app. */
  application_url?: string
}

/**
 * Patch body shape used until the generated `PatchedApplicationUpdate` type
 * picks up `app_url` (and any other fields) from the regenerated schema.
 */
export type ExtendedPatchedApplicationUpdate = {
  name?: string
  description?: string
  domain?: string | null
  app_url?: string | null
  icon?: number | null
  settings?: Record<string, unknown> | null
}

export const applicationsThirdPartyCreate = async (
  body: ApplicationsThirdPartyCreateBody
): Promise<ApplicationDetail> => {
  const { data } = await client.post<ApplicationDetail>({
    responseType: 'json',
    security: [{ name: 'Authorization', type: 'apiKey' }],
    url: '/applications/third-party/',
    body,
    headers: { 'Content-Type': 'application/json' }
  })

  return data as unknown as ApplicationDetail
}

export const applicationsThirdPartyCreateMutationOptions =
  (): UseMutationOptions<
    ApplicationDetail,
    AxiosError<unknown>,
    ApplicationsThirdPartyCreateBody
  > => ({
    mutationFn: applicationsThirdPartyCreate
  })

export const useApplicationsThirdPartyCreate = () =>
  useMutation(applicationsThirdPartyCreateMutationOptions())
